import React, { useEffect, useId, useRef, useState } from 'react';
import './SingleSelect.css';

interface Props {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

const SingleSelect: React.FC<Props> = ({ label, value, options, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const selected = options.findIndex((option) => option.value === value);
  const close = () => { setOpen(false); triggerRef.current?.focus(); };

  useEffect(() => { if (open) optionRefs.current[active]?.focus(); }, [open, active]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  return <div ref={rootRef} className="single-select" onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }} onKeyDown={(event) => {
    if (disabled) return;
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(); }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : !open ? Math.max(0, selected) : (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
      setOpen(true);
    }
  }}>
    <button ref={triggerRef} type="button" className="single-select-trigger" aria-label={label} disabled={disabled}
      aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? menuId : undefined}
      onClick={() => { setActive(Math.max(0, selected)); setOpen(!open); }}>
      <span>{options[selected]?.label}</span><svg className={open ? 'is-open' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
    {open && !disabled && <div id={menuId} role="listbox" aria-label={label} className="single-select-menu">
      {options.map((option, index) => <button key={option.value} ref={(node) => { optionRefs.current[index] = node; }} type="button"
        role="option" aria-selected={option.value === value} tabIndex={index === active ? 0 : -1}
        onFocus={() => setActive(index)} onClick={() => { close(); onChange(option.value); }}>
        <span>{option.label}</span>{option.value === value && <span aria-hidden="true">✓</span>}
      </button>)}
    </div>}
  </div>;
};

export default SingleSelect;
